import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9ng5v0bdkpk99j7gj4g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      if (res.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.meta as Record<string, string>)?.detail
          || (parsed?.message as string)
          || responseText
          || '提交失败，请稍后再试';

        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后再试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  const brandValues = [
    {
      icon: 'ri-eye-line',
      title: '极致体验',
      desc: '4K高清画质配合智能CDN加速，确保每一帧都清晰流畅，即点即播无需等待缓冲。',
    },
    {
      icon: 'ri-database-2-line',
      title: '海量资源',
      desc: '收录超过10万部电影、电视剧、综艺、动漫和纪录片，每日持续更新最新内容。',
    },
    {
      icon: 'ri-user-heart-line',
      title: '用户至上',
      desc: '完全免费无广告打扰，简洁界面设计，让每一位用户都能享受纯粹的观影体验。',
    },
    {
      icon: 'ri-shield-star-line',
      title: '安全可靠',
      desc: '严格的内容审核机制和用户隐私保护，打造绿色健康的在线观影环境。',
    },
  ];

  const timeline = [
    { year: '2020', title: '平台创立', desc: '蚂蚁影视正式上线，致力于为用户提供免费高清影视内容。' },
    { year: '2021', title: '内容爆发', desc: '内容库突破5万部，覆盖电影、电视剧、综艺、动漫、纪录片五大领域。' },
    { year: '2022', title: '技术升级', desc: '全面升级播放技术，实现4K超清画质和智能CDN加速，用户体验大幅提升。' },
    { year: '2023', title: '用户突破', desc: '月活跃用户突破3000万，成为国内领先的免费在线影视平台之一。' },
    { year: '2024', title: '持续创新', desc: 'AI智能推荐系统上线，个性化内容推荐让每位用户都能找到心仪的影视作品。' },
  ];

  const testimonials = [
    {
      name: '小王',
      text: '自从用了蚂蚁影视，追剧再也不用到处找资源了！更新快，画质好，关键是免费，太香了！',
      role: '追剧达人',
    },
    {
      name: '李女士',
      text: '蚂蚁影视的画质真的没话说，4K超清看起来就是不一样。而且即点即播，完全不用等缓冲。',
      role: '影视爱好者',
    },
    {
      name: '张同学',
      text: '动漫资源特别全，每周追的新番都有。加载速度比我用过的其他平台都快，强烈推荐！',
      role: '动漫迷',
    },
    {
      name: '刘先生',
      text: '全家都在蚂蚁影视上看剧，爸妈看电视剧，我看电影，孩子看动画片，一个平台全搞定。',
      role: '家庭用户',
    },
  ];

  return (
    <>
      <div className="pt-20">
        <div className="px-4 md:px-6">
          <div className="max-w-[1200px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <main>
          <section className="px-4 md:px-6 py-10 md:py-16">
            <div className="max-w-[1200px] mx-auto">
              <div className="text-center max-w-3xl mx-auto mb-12">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                  关于蚂蚁影视
                </h1>
                <p className="text-base text-foreground-600 leading-relaxed">
                  蚂蚁影视成立于2020年，是国内领先的高清免费在线影视平台。我们秉持"让每个人都享受高品质观影体验"的理念，
                  致力于为用户提供海量、高清、流畅的在线影视内容。从电影到电视剧，从综艺到动漫，从纪录片到体育赛事，
                  蚂蚁影视始终以用户需求为核心，不断优化产品体验，已成为数千万用户信赖的在线追剧首选平台。
                </p>
              </div>

              <div className="text-center max-w-4xl mx-auto mb-12">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20home%20theater%20setup%20with%20large%20screen%20displaying%20colorful%20cinematic%20content%2C%20cozy%20living%20room%20atmosphere%2C%20warm%20ambient%20lighting%2C%20comfortable%20seating%2C%20clean%20contemporary%20design%2C%20cinematic%20mood&width=1200&height=500&seq=about-banner-1&orientation=landscape"
                  alt="蚂蚁影视品牌形象 - 舒适的居家观影体验"
                  title="蚂蚁影视 - 高清免费在线观看平台"
                  className="w-full rounded-lg object-cover"
                  style={{ height: '320px' }}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {brandValues.map((item, i) => (
                  <div
                    key={i}
                    className="p-5 rounded-lg bg-background-100 border border-background-200/70 text-center"
                  >
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className={`${item.icon} text-xl`} />
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 md:py-16 bg-background-100">
            <div className="max-w-[1200px] mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  发展历程
                </h2>
                <p className="text-sm text-foreground-500">蚂蚁影视的成长之路</p>
              </div>

              <div className="relative">
                <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2" />

                <div className="space-y-6">
                  {timeline.map((item, i) => (
                    <div
                      key={i}
                      className={`flex flex-col lg:flex-row gap-4 lg:gap-8 items-start ${
                        i % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${i % 2 === 0 ? 'lg:text-right' : 'lg:text-left'}`}>
                        <div className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                          <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-accent-100 text-accent-700 mb-2 whitespace-nowrap">
                            {item.year}
                          </span>
                          <h3 className="text-base font-semibold text-foreground-900 mb-1">{item.title}</h3>
                          <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                      <div className="hidden lg:flex w-8 justify-center flex-shrink-0 pt-6">
                        <div className="w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50" />
                      </div>
                      <div className="flex-1 hidden lg:block" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 md:py-16">
            <div className="max-w-[1200px] mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  用户口碑
                </h2>
                <p className="text-sm text-foreground-500">听听他们怎么说</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {testimonials.map((item, i) => (
                  <div
                    key={i}
                    className="p-5 rounded-lg bg-background-100 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, j) => (
                        <i key={j} className="ri-star-fill text-accent-500 text-sm" />
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      &ldquo;{item.text}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-semibold text-sm">
                        {item.name[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">{item.name}</p>
                        <p className="text-xs text-foreground-400">{item.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 md:py-16 bg-background-100">
            <div className="max-w-[1200px] mx-auto">
              <div className="flex flex-col lg:flex-row gap-10">
                <div className="flex-1">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-500 mb-6 leading-relaxed">
                    如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系。我们的团队将在24小时内回复您的消息。
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                        <p className="text-sm text-foreground-500">contact@mayifilm.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-customer-service-line" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">客服热线</p>
                        <p className="text-sm text-foreground-500">400-888-6666</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-time-line" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">工作时间</p>
                        <p className="text-sm text-foreground-500">周一至周日 9:00 - 22:00</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex-1">
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="p-6 rounded-lg bg-background-50 border border-background-200/70 space-y-4"
                  >
                    <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">
                      发送消息
                    </h3>

                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1">
                        主题
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入消息主题"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                        留言内容
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      />
                    </div>

                    <input
                      type="text"
                      name="phone_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      发送消息
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">
                        消息已发送成功，我们会尽快回复您！
                      </p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-600 text-center">
                        {formError}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于蚂蚁影视',
            description: '蚂蚁影视高清免费在线观看平台，成立于2020年，致力于为用户提供海量高清流畅的在线影视内容。',
            url: 'https://www.mayifilm.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '蚂蚁影视',
              url: 'https://www.mayifilm.com/',
            },
          }),
        }}
      />
    </>
  );
}